import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React, { useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

type Props = {
  open: boolean;
  label?: string;
  /** Field aspect ratio (width / height). The captured PNG is drawn stretched to
   * the field box on screen and in the PDF, so the pad matches the field's shape
   * (scaled up) to keep the signature undistorted. */
  aspect: number;
  /** Brand color for the Save button; falls back to the app primary. */
  primaryColor?: string;
  onSave: (dataUrl: string) => void;
  onClose: () => void;
  onCaptureError?: (err: unknown) => void;
};

/**
 * Full-screen signing modal for a custom-form signature field. Clicking the field
 * on any fill surface (patient portal or staff Generate/Review) opens this so the
 * patient/staff can sign in a large pad instead of the small inline box.
 */
const SignatureFieldModal: React.FC<Props> = ({
  open,
  label,
  aspect,
  primaryColor,
  onSave,
  onClose,
  onCaptureError,
}) => {
  const padRef = useRef<SignatureCanvas>(null);
  const [signed, setSigned] = useState(false);

  // Size the pad to the field's aspect ratio, scaled up and clamped to the
  // viewport so it stays a comfortable signing area on phone/kiosk and desktop.
  const { w, h } = useMemo(() => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 640;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const safeAspect = aspect > 0 ? aspect : 3;
    let width = Math.min(620, vw - 48);
    let height = Math.round(width / safeAspect);
    const maxH = Math.min(400, vh - 240);
    if (height > maxH) {
      height = maxH;
      width = Math.round(height * safeAspect);
    }
    return { w: Math.max(width, 160), h: Math.max(height, 120) };
  }, [aspect]);

  if (!open) return null;

  const handleClear = () => {
    padRef.current?.clear();
    setSigned(false);
  };

  const handleSave = () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) return;
    try {
      // Full pad (not getTrimmedCanvas) so the mark isn't zoomed when redrawn into
      // the fixed field box (the PDF draws it stretched to that box).
      const dataUrl = pad.getCanvas().toDataURL('image/png');
      onSave(dataUrl);
    } catch (err) {
      onCaptureError?.(err);
    }
  };

  // Rendered inline (not portaled to document.body): a Radix modal Dialog parent
  // (the staff Generate modal) sets `pointer-events: none` on the body, which would
  // freeze a body-portaled overlay. Inline keeps it inside the parent's interactive
  // region, and `fixed inset-0` still covers the viewport on the (transform-free)
  // patient portal. pointer-events-auto guards against an inherited `none`.
  return (
    <div
      className="pointer-events-auto fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <h3 className="truncate text-base font-semibold text-gray-900">{label || 'Signature'}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <Icon icon={Icons.close} className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-3 text-sm text-gray-500">Sign in the box below.</p>

        <div className="flex justify-center">
          <div
            className="rounded-md border border-gray-300 bg-white"
            style={{ width: w, height: h }}
          >
            <SignatureCanvas
              ref={padRef}
              clearOnResize={false}
              onEnd={() => setSigned(true)}
              canvasProps={{ width: w, height: h, className: 'touch-none rounded-md' }}
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm font-medium text-gray-500 underline hover:text-gray-700"
          >
            Clear
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!signed}
              style={primaryColor ? { backgroundColor: primaryColor } : undefined}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureFieldModal;
